import React from 'react';

import { DateTime } from 'luxon';

import { titleize } from '@admin/components/helpers/titleize';
import { MessagesQuery } from '@admin/schema';
import { formatPhoneForHuman } from '@admin/utils/format_phone_for_human';

import { PhoneWithName } from './types';

type MessageFromQuery = NonNullable<MessagesQuery['paginatedMessages']>['results'][0];
type PhoneFromQuery = MessageFromQuery['phone'];
type UserFromQuery = MessageFromQuery['user'];

interface MessageMetadataProps {
  message: MessageFromQuery;
  phoneList: PhoneWithName[];
}

const formatPhoneDetail = (phone: PhoneFromQuery, phoneList: PhoneWithName[]): string => {
  if (phone && phone.number) {
    let detail = formatPhoneForHuman(phone.number);

    // Try to find enhanced name from phoneList first
    const enhancedPhone = phoneList.find((p) => String(p.id) === String(phone.id));
    const displayName = enhancedPhone?.name || phone.name;

    if (displayName) {
      detail = `${detail} (${displayName})`;
    }

    return detail;
  }
  return '';
};

const formatPhoneAndTealUser = (number: string, user: UserFromQuery): string => {
  return `${number === 'iterable' ? 'Iterable' : formatPhoneForHuman(number)} (${user.name})`;
};

const isInbound = (message: MessageFromQuery): boolean => {
  return message.kind === 'inbound';
};

const getSender = (message: MessageFromQuery, phoneList: PhoneWithName[]): string => {
  return isInbound(message)
    ? formatPhoneDetail(message.phone, phoneList)
    : formatPhoneAndTealUser(message.from || '', message.user);
};

const getRecipient = (message: MessageFromQuery, phoneList: PhoneWithName[]): string => {
  return isInbound(message)
    ? formatPhoneAndTealUser(message.to || '', message.user)
    : formatPhoneDetail(message.phone, phoneList);
};

// e.g. "Sep 16, 2025, 1:45 PM"
const formatMessageDate = (dateString: string): string => {
  return DateTime.fromISO(dateString).toLocaleString(DateTime.DATETIME_MED);
};

export const MessageMetadata: React.FC<MessageMetadataProps> = ({ message, phoneList }) => {
  return (
    <span>
      <span>{titleize(message.kind)}:</span>
      {' from '}
      <strong>{getSender(message, phoneList)}</strong>
      {' to '}
      <strong>{getRecipient(message, phoneList)}</strong>
      {' on '}
      <span>{formatMessageDate(message.createdAt)}</span>
    </span>
  );
};
